import { useEffect, useRef, useState } from "react";
import Button from "@components/ui/Button";
import ErrorBanner from "@components/shared/ErrorBanner";
import {
  createVisitType, fetchClinicProviders, fetchLearning, markProfileReviewed, runKeyCheck, runLearning,
  setClinicProviderRole, setConnectedFlow,
} from "@services/clinicProviders";

const day = (iso) => (iso ? String(iso).slice(0, 10) : "");

/**
 * What the connected inbound flow learned from the clinic's history, and
 * the switches that gate it: the one-time review, the connected flow
 * itself (the server refuses it before the review or while the Harbor key
 * check fails, and its reason is shown as is), and the key check.
 *
 * Every action saves at once; the drawer's Save button is not involved.
 */
export default function LearningPanel({ clinicId }) {
  const [summary, setSummary] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addedTypes, setAddedTypes] = useState([]);
  const clinicRef = useRef(clinicId);

  useEffect(() => {
    let cancelled = false;
    clinicRef.current = clinicId;
    setLoading(true);
    setError(null);
    setSummary(null);
    setProviders([]);
    setAddedTypes([]);
    if (!clinicId) { setLoading(false); return undefined; }
    (async () => {
      try {
        const data = await fetchLearning(clinicId);
        if (!cancelled) setSummary(data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load what was learned");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    (async () => {
      // Names for the suggestions; without them the panel still works.
      try {
        const rows = await fetchClinicProviders(clinicId);
        if (!cancelled) setProviders(rows);
      } catch {
        if (!cancelled) setProviders([]);
      }
    })();
    return () => { cancelled = true; };
  }, [clinicId]);

  const run = async (write, apply) => {
    const forClinic = clinicId;
    setBusy(true);
    setError(null);
    try {
      const result = await write();
      if (clinicRef.current !== forClinic) return;
      apply(result);
    } catch (e) {
      if (clinicRef.current !== forClinic) return;
      setError(e.message || "Could not save the change");
    } finally {
      setBusy(false);
    }
  };

  const replaceSummary = (data) => { if (data) setSummary(data); };

  const learnNow = () => run(() => runLearning(clinicId), replaceSummary);
  const markReviewed = () => run(() => markProfileReviewed(clinicId), replaceSummary);

  const toggleConnected = () => {
    const enabled = !summary?.connected_flow_enabled;
    run(
      () => setConnectedFlow({ clinicId, enabled }),
      (data) => setSummary((s) => ({ ...s, connected_flow_enabled: !!data?.connected_flow_enabled })),
    );
  };

  const checkKey = () =>
    run(
      () => runKeyCheck({ clinicId, probeWrites: true }),
      (data) => setSummary((s) => ({ ...s, key_check: data })),
    );

  const acceptRole = (externalProviderId, role) =>
    run(
      () => setClinicProviderRole({ clinicId, externalProviderId, role }),
      (updated) => {
        setProviders((rows) => rows.map((r) => (r.external_provider_id === externalProviderId
          ? { ...r, ...(updated || {}), role } : r)));
      },
    );

  const addType = (label) =>
    run(
      () => createVisitType({
        clinicId,
        fields: { name: label, phrases: [label.toLowerCase()], length_minutes: 30, default_role: "dentist" },
      }),
      () => setAddedTypes((list) => [...list, label]),
    );

  const nameOf = (id) => providers.find((p) => p.external_provider_id === id)?.name || id;
  const roleOf = (id) => providers.find((p) => p.external_provider_id === id)?.role ?? null;
  const suggestions = Object.entries(summary?.providers || {})
    .filter(([id, prof]) => prof?.role_suggestion && providers.some((p) => p.external_provider_id === id)
      && prof.role_suggestion !== roleOf(id));
  const keyCheck = summary?.key_check;

  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <div>
        <div className="text-sm font-semibold">Learning and the connected flow</div>
        <p className="text-xs text-gray-500">
          Roles, rooms and visit lengths are learned from the clinic's appointment history once a day.
          Look the result over before the agent books by itself.
        </p>
      </div>
      <ErrorBanner message={error} onDismiss={() => setError(null)} />
      {loading && <p className="text-sm text-gray-500">Loading…</p>}
      {!loading && summary && (
        <>
          {summary.inferred_at ? (
            <div className="text-sm text-gray-700 space-y-1">
              <p>{`Coverage: ${summary.coverage_from || "?"} to ${summary.coverage_to || "?"} (${summary.months ?? "?"} months).`}</p>
              <p>{`Last run ${day(summary.inferred_at)}.`}</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Not learned yet.</p>
          )}
          {(summary.warnings || []).length > 0 && (
            <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 space-y-1">
              {summary.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          )}
          {suggestions.length > 0 && (
            <ul className="text-sm space-y-1">
              {suggestions.map(([id, prof]) => (
                <li key={id} className="flex flex-wrap items-center gap-2">
                  <span>{`${nameOf(id)} looks like a ${prof.role_suggestion}.`}</span>
                  <Button color="light" size="xs" disabled={busy}
                          aria-label={`Accept ${prof.role_suggestion} for ${nameOf(id)}`}
                          onClick={() => acceptRole(id, prof.role_suggestion)}>Accept</Button>
                </li>
              ))}
            </ul>
          )}
          {(summary.suggested_types || []).length > 0 && (
            <ul className="text-sm space-y-1">
              {summary.suggested_types.map((t) => (
                <li key={t.label} className="flex flex-wrap items-center gap-2">
                  <span>{`${t.label}: ${t.count} visits`}</span>
                  {addedTypes.includes(t.label) ? (
                    <span className="text-xs text-green-700">Added</span>
                  ) : (
                    <Button color="light" size="xs" disabled={busy}
                            aria-label={`Add ${t.label} as a visit type`}
                            onClick={() => addType(t.label)}>Add as visit type</Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button color="light" size="xs" disabled={busy} onClick={learnNow}>Run learning now</Button>
            <Button color="light" size="xs" disabled={busy} onClick={markReviewed}>Mark reviewed</Button>
            <span className="text-xs text-gray-600">
              {summary.profile_reviewed_at ? `Reviewed on ${day(summary.profile_reviewed_at)}` : "Not reviewed yet"}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <input type="checkbox" aria-label="Connected flow" checked={!!summary.connected_flow_enabled}
                   disabled={busy} onChange={toggleConnected} />
            <span>The agent books, moves and creates patients in the PMS itself</span>
          </div>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Button color="light" size="xs" disabled={busy} onClick={checkKey}>Check key</Button>
              <span className="text-xs text-gray-600">
                {!keyCheck ? "Key not checked yet"
                  : keyCheck.ok ? `Key check passed ${day(keyCheck.checked_at)}`
                    : `Key check failed ${day(keyCheck.checked_at)}${keyCheck.error ? ` (${keyCheck.error})` : ""}`}
              </span>
            </div>
            {keyCheck?.scopes && (
              <ul className="text-xs text-gray-700 flex flex-wrap gap-3">
                {Object.entries(keyCheck.scopes).map(([scope, state]) => (
                  <li key={scope}>{`${scope}: ${state}`}</li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
